import type { MailingListSubscriberMetrics } from "./types";
import { formatNumber } from "./format";

const groups = [
  { key: "current_members", label: "Huidige leden", tone: "member" },
  { key: "former_members", label: "Voormalige leden", tone: "former" },
  { key: "viewers", label: "Meekijkers", tone: "viewer" },
  { key: "non_members", label: "Niet-leden", tone: "nonmember" },
] as const;

const percent = (count: number, total: number) => total > 0 ? count / total * 100 : 0;
const percentage = (value: number) => `${value.toLocaleString("nl-NL", { maximumFractionDigits: 1 })}%`;

export function MailingAudienceBreakdown({ metrics, error, showOverlap = false, showCryptoOverlap = false, waitlist = false, open, onToggle }: {
  metrics: MailingListSubscriberMetrics | null;
  error: string | null;
  showOverlap?: boolean;
  showCryptoOverlap?: boolean;
  waitlist?: boolean;
  open: boolean;
  onToggle: () => void;
}) {
  const audience = metrics?.audience;
  const total = metrics?.current_subscribers ?? 0;
  const available = audience && groups.every(({ key }) => Number.isInteger(audience[key]) && audience[key] >= 0) &&
    groups.reduce((sum, { key }) => sum + audience[key], 0) === total;
  const overlap = metrics?.escape_overlap;
  const overlapAvailable = overlap !== undefined && Number.isInteger(overlap) && overlap >= 0 && overlap <= total;
  const cryptoOverlap = metrics?.crypto10x_overlap;
  const cryptoOverlapAvailable = typeof cryptoOverlap === "number" && Number.isInteger(cryptoOverlap) && cryptoOverlap >= 0 && cryptoOverlap <= total;
  const visibleGroups: ReadonlyArray<{ key: typeof groups[number]["key"]; label: string; tone: string }> = waitlist ? [
    { key: "non_members", label: "Potentiële nieuwe leden", tone: "nonmember" },
    groups[1],
    ...groups.filter(({ key }) => ["current_members", "viewers"].includes(key) && (audience?.[key] ?? 0) > 0),
  ] : groups;

  return (
    <section className="mailing-audience" aria-label="Samenstelling e-maillijst">
      <details className="mailing-audience-details" open={open}>
        <summary onClick={(event) => { event.preventDefault(); onToggle(); }}>Relatie met Brede Horizon</summary>
        {available ? <>
          <div className="mailing-audience-bar" role="img" aria-label={visibleGroups.map(({ key, label }) => `${label}: ${formatNumber(audience[key])}, ${percentage(percent(audience[key], total))}`).join(". ")}>
            {visibleGroups.map(({ key, label, tone }) => (
              <i key={key} className={`audience-${tone}`} style={{ width: `${percent(audience[key], total)}%` }} title={`${label}: ${formatNumber(audience[key])}`} />
            ))}
          </div>
          <dl className="mailing-audience-legend">
            {visibleGroups.map(({ key, label, tone }) => (
              <div key={key}>
                <dt><i className={`audience-${tone}`} aria-hidden="true" />{label}</dt>
                <dd><b>{formatNumber(audience[key])}</b><small>{percentage(percent(audience[key], total))}</small></dd>
              </div>
            ))}
          </dl>
          {total === 0 && <p className="mailing-audience-note">Geen actieve inschrijvingen.</p>}
          {waitlist && audience.current_members + audience.viewers > 0 && <p className="mailing-audience-note">Er staan ook huidige leden of meekijkers op de wachtlijst. Deze zijn apart geteld.</p>}
        </> : <p className="mailing-audience-note" role="status">
          {!metrics && !error ? "Ledenverdeling laden…" : "Ledenverdeling niet beschikbaar."}
        </p>}


        <details className="mailing-audience-explanation">
          <summary>Hoe wordt dit geteld?</summary>
          <p>Per uniek, actief ingeschreven e-mailadres. We koppelen alle bekende e-mailadressen aan de ledenadministratie, aangevuld met de bestaande ActiveCampaign-leden-, meekijker- en voormalig-ledenlijsten. De lidmaatschapsstatus in de beheertool gaat voor.</p>
          <p>{waitlist ? "Potentiële nieuwe leden hebben geen bekend huidig of vroeger lidmaatschap en zijn geen actieve meekijker. Wie ooit lid was, telt als voormalig lid, ook als die nu meekijkt. Huidige leden tellen apart." : "Iedereen telt één keer: huidig lid, anders actieve meekijker, anders voormalig lid, anders niet-lid. Niet-leden hebben geen gevonden lidmaatschap of actieve meekijkstatus."} Huidige leden omvatten gratis- en barterleden en opzeggers tot hun einddatum. Een onbekend ander e-mailadres kan niet worden gekoppeld.</p>
          <p>De lijsten worden maximaal 5 minuten bewaard voor deze meting.{showOverlap ? " De overlap telt actieve inschrijvingen op beide lijsten en staat los van de vier groepen hierboven." : ""}</p>
        </details>
      </details>

      {showOverlap && <div className="mailing-audience-overlap">
        <div className="mailing-overlap-heading">
          <h3>Ook op Escape 925</h3>
          {overlapAvailable && <b>{formatNumber(overlap)} <small>{percentage(percent(overlap, total))}</small></b>}
        </div>
        {overlapAvailable ? <>
          <div className="mailing-audience-bar mailing-overlap-bar" role="img" aria-label={`${formatNumber(overlap)} van ${formatNumber(total)} contacten staan ook actief op Escape 925`}>
            <i style={{ width: `${percent(overlap, total)}%` }} />
          </div>
          <p className="mailing-audience-note">{formatNumber(total - overlap)} staan niet op Escape 925</p>
        </> : <p className="mailing-audience-note" role="status">{!metrics && !error ? "Overlap laden…" : "Overlap niet beschikbaar."}</p>}
      </div>}

      {showCryptoOverlap && <div className="mailing-audience-overlap">
        <div className="mailing-overlap-heading"><h3>Ook op Crypto 10x – Escape 925</h3>
          {cryptoOverlapAvailable && <b>{formatNumber(cryptoOverlap)} <small>{percentage(percent(cryptoOverlap, total))}</small></b>}
        </div>
        {cryptoOverlapAvailable ? <>
          <div className="mailing-audience-bar mailing-overlap-bar" role="img" aria-label={`${formatNumber(cryptoOverlap)} van ${formatNumber(total)} contacten staan ook actief op Crypto 10x – Escape 925`}><i style={{ width: `${percent(cryptoOverlap, total)}%` }} /></div>
          <p className="mailing-audience-note">{formatNumber(total - cryptoOverlap)} staan niet op deze downloadlijst</p>
        </> : <p className="mailing-audience-note" role="status">{!metrics && !error ? "Overlap laden…" : "Overlap niet beschikbaar."}</p>}
      </div>}
    </section>
  );
}
